import { forwardRef, useCallback, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { isValidHexColor } from "@tellescope/utils";
import { cn } from "../lib/utils";
import { Input, Popover } from "@tellescope/design-system";
import { GetColorName } from "hex-color-to-color-name";
import { Check, ChevronDown, Plus, X } from "lucide-react";

const HEX_PATTERN = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

const SWATCH_BASE = cn(
  "relative flex size-7 items-center justify-center rounded-full outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border border-gray-200"
);

const SELECTED_RING = cn("ring-2 ring-ring ring-offset-2");

const CUSTOM_SWATCH_GRADIENT =
  "conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)";

const normalizeHex = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("#") ? trimmed.toUpperCase() : `#${trimmed.toUpperCase()}`;
};

export interface ColorPickerProps {
  value?: string;
  onValueChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  "aria-label"?: string;
  showName?: boolean;
  presetColors?: string[];
  swatchClassName?: string;
  clearable?: boolean;
}

export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>(
  (
    {
      value,
      onValueChange,
      onBlur,
      disabled,
      placeholder = "#000000",
      className,
      id,
      showName = false,
      presetColors,
      swatchClassName,
      clearable = false,
      ...ariaProps
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [inputDraft, setInputDraft] = useState<string | null>(null);

    const currentValue = value ?? "";
    const pickerColor = HEX_PATTERN.test(currentValue) ? normalizeHex(currentValue) : "#000000";

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setInputDraft(null);
          onBlur?.();
        }
      },
      [onBlur]
    );

    const handlePickerChange = useCallback(
      (nextColor: string) => {
        setInputDraft(null);
        onValueChange?.(nextColor.toUpperCase());
      },
      [onValueChange]
    );

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        setInputDraft(raw);
        if (HEX_PATTERN.test(raw)) {
          onValueChange?.(normalizeHex(raw));
        }
      },
      [onValueChange]
    );

    const handleInputBlur = useCallback(() => {
      if (inputDraft === null) return;
      if (inputDraft === "") {
        onValueChange?.("");
      }
      setInputDraft(null);
    }, [inputDraft, onValueChange]);

    const inputValue = inputDraft ?? currentValue;

    const popoverContent = (
      <Popover.Content className="w-auto p-3" align="start" side="bottom">
        <div className="flex flex-col gap-3">
          <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            className="h-9 font-mono"
            aria-label="Hex color value"
          />
        </div>
      </Popover.Content>
    );

    if (presetColors) {
      const isCustom =
        !!currentValue &&
        isValidHexColor(currentValue) &&
        !presetColors.some((preset) => preset.toLowerCase() === currentValue.toLowerCase());

      return (
        <div className={cn("flex flex-wrap items-center gap-3", className)}>
          {presetColors.map((preset) => {
            const isSelected = !!currentValue && currentValue.toLowerCase() === preset.toLowerCase();
            return (
              <button
                key={preset}
                type="button"
                disabled={disabled}
                aria-label={preset}
                aria-pressed={isSelected}
                onClick={() => onValueChange?.(clearable && isSelected ? "" : preset)}
                className={cn(SWATCH_BASE, isSelected && SELECTED_RING, swatchClassName)}
                style={{ backgroundColor: preset }}
              >
                {isSelected && <Check className="size-4 text-white drop-shadow" />}
              </button>
            );
          })}
          <Popover open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger asChild>
              <button
                ref={ref}
                id={id}
                type="button"
                disabled={disabled}
                aria-label="Custom color"
                aria-pressed={isCustom}
                className={cn(SWATCH_BASE, isCustom && SELECTED_RING, swatchClassName)}
                style={{ background: isCustom ? currentValue : CUSTOM_SWATCH_GRADIENT }}
                {...ariaProps}
              >
                {isCustom ? (
                  <Check className="size-4 text-white drop-shadow" />
                ) : (
                  <Plus className="size-4 text-white drop-shadow" />
                )}
              </button>
            </Popover.Trigger>
            {popoverContent}
          </Popover>
          {clearable && isCustom && (
            <button
              type="button"
              disabled={disabled}
              aria-label="Clear color"
              onClick={() => onValueChange?.("")}
              className="flex size-7 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 -ml-1"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      );
    }

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            data-slot="color-picker-trigger"
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none transition-colors justify-between",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
              className
            )}
            {...ariaProps}
          >
            <div className="flex gap-2 items-center">
              {!!currentValue && (
                <span
                  data-slot="color-picker-swatch"
                  className="size-3 shrink-0 rounded-full"
                  style={{ backgroundColor: currentValue || "transparent" }}
                />
              )}
              <span className={cn(!currentValue && "text-muted-foreground")}>
                {(showName && currentValue ? `${GetColorName(currentValue)} (${currentValue})` : currentValue) ||
                  placeholder}
              </span>
            </div>
            <ChevronDown size={16} className="text-muted-foreground" />
          </button>
        </Popover.Trigger>
        {popoverContent}
      </Popover>
    );
  }
);

ColorPicker.displayName = "ColorPicker";
